/**
 * The user's apps, grouped by category, for the Agent Hub.
 *
 * Same endpoint the sidebar uses (/api/sidebar/user-menu) and deliberately so: the hub
 * must offer exactly what the sidebar offers, or a card opens a route RouteGuard then
 * refuses. What it does NOT reuse is useSidebarMenu's mapping, which flattens each app
 * through mapAppToMenuItem and drops `description` — the one field the hub's cards and
 * the breadcrumb subtitle are built on.
 *
 * Failure renders an empty hub rather than an error screen, matching the sidebar's
 * existing behaviour on the same request. The error is reported, not shown.
 */

import { useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '@/utils/authToken';
import { httpFetch } from '@/lib/httpClient';
import { reportError } from '@/observability/clientLogger';
import { dedupedFetch, invalidateCached } from '@/lib/requestCache';

/**
 * Category display order: the six design categories first, anything else after in menu
 * order, so a category added in Mongo appears instead of vanishing. The agenthub-overview
 * app keeps the same list for its sections; Settings uses this one.
 */
export const CATEGORY_ORDER = [
  'Compliance',
  'Voyage & Emission',
  'Procurement',
  'Technical & Maintenance',
  'Fuel, Lube & Chemicals',
  'Crew & Personnel',
];

export interface HubApp {
  id: string;
  label: string;
  description?: string;
  href: string;
  icon?: string;
  category?: string;
  type?: 'iframe' | 'app';
  url?: string;
}

export interface HubCategory {
  name: string;
  apps: HubApp[];
}

export interface UseAgentHubAppsResult {
  apps: HubApp[];
  categories: HubCategory[];
  isLoading: boolean;
}

/** Apps with no category still have to appear somewhere. */
const UNCATEGORISED = 'Other';

/**
 * The menu request is shared with the sidebar through src/lib/requestCache: the hub page
 * mounts this hook and the SimpleLayout inside it mounts it again for the breadcrumb, and
 * useSidebarMenu fetches the same endpoint a third time.
 */

/** Exported for the case where a menu change must be reflected without a reload. */
export function invalidateAgentHubApps(): void {
  invalidateCached('user-menu:');
}

export function useAgentHubApps(
  tenant: string | null,
  userId: string | null | undefined,
): UseAgentHubAppsResult {
  const [apps, setApps] = useState<HubApp[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchMenu = async () => {
      if (!tenant || !userId) {
        setIsLoading(false);
        return;
      }

      try {
        const apps = await dedupedFetch(`user-menu:${tenant}:${userId}`, async () => {
          // localhost → Bearer header; deployed → null, the HttpOnly cookie authenticates.
          const authToken = getAuthToken();
          const response = await httpFetch(`/api/sidebar/user-menu?userId=${userId}`, {
            headers: {
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
              'x-node': tenant,
            },
          });

          if (!response.ok) {
            const body = await response.json().catch(() => null);
            throw new Error(body?.error || 'User-menu request returned an error body');
          }

          const data = await response.json();
          return (data.apps ?? data ?? []) as HubApp[];
        });
        if (!cancelled) setApps(apps);
      } catch (error) {
        reportError(error, { event_detail: 'agent_hub_menu_fetch_failed' });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMenu();
    return () => {
      cancelled = true;
    };
  }, [tenant, userId]);

  /**
   * Grouping preserves the server's ordering inside each category — it sorts by `order`,
   * which is the sidebar ordering the user already knows. Only the CATEGORIES are
   * reordered, and only those CATEGORY_ORDER names; anything else keeps the order it
   * first appeared in, so a category added in Mongo shows up at the end rather than
   * being dropped for not being on a list.
   */
  const categories = useMemo<HubCategory[]>(() => {
    const byName = new Map<string, HubApp[]>();
    for (const app of apps) {
      const name = app.category?.trim() || UNCATEGORISED;
      const bucket = byName.get(name);
      if (bucket) bucket.push(app);
      else byName.set(name, [app]);
    }

    const rank = (name: string) => {
      const i = CATEGORY_ORDER.findIndex((c) => c.toLowerCase() === name.toLowerCase());
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };

    return [...byName.entries()]
      .map(([name, list]) => ({ name, apps: list }))
      .sort((a, b) => rank(a.name) - rank(b.name));
  }, [apps]);

  return { apps, categories, isLoading };
}

/** The app whose `href` matches a pathname, for the breadcrumb and the app switcher. */
export function findAppByPath(apps: HubApp[], pathname: string): HubApp | undefined {
  return apps.find((app) => app.href === pathname);
}
